import axios from "axios";
import { getBackendUrl, extractErrorMessage, buildScopedHeaders } from "./apiHelper";
import {
  getActiveChatbotId,
  getPublicWidgetSession,
  setPublicWidgetSession,
  isPublicWidgetSessionExpired,
} from "./authUtils";

const BACKEND_URL = getBackendUrl();

function isValidUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || '').trim()
  );
}

function sanitizeChatbotId(value = null) {
  return isValidUuid(value) ? String(value).trim() : null;
}

function sanitizeChatPayload(payload = {}, { isPublicRequest = false } = {}) {
  const {
    userAuthToken,
    publicVisitorToken,
    chatbotId,
    publicBotId,
    tenantId,
    ...rest
  } = payload;

  if (isPublicRequest) {
    return {
      ...rest,
      ...(publicBotId ? { publicBotId } : {}),
    };
  }

  return {
    ...rest,
    ...(chatbotId ? { chatbotId } : {}),
    ...(tenantId ? { tenantId } : {}),
  };
}

function getCurrentPublicContextBotId() {
  if (typeof window === "undefined") return null;
  return window.__CHATBOT_PUBLIC_CONTEXT__?.publicBotId || null;
}

function getScopedPublicSession(publicBotId = null) {
  const resolvedPublicBotId = publicBotId || getCurrentPublicContextBotId();
  if (!resolvedPublicBotId) {
    return null;
  }

  const session = getPublicWidgetSession({ publicBotId: resolvedPublicBotId });
  if (session?.publicBotId && session.publicBotId !== resolvedPublicBotId) {
    return null;
  }

  return session;
}

export async function sendUserInputToChatbot(payload) {
  try {
    const contextPublicBotId = getCurrentPublicContextBotId();
    const explicitPublicBotId = payload?.publicBotId || contextPublicBotId || null;
    let storedPublicSession = getScopedPublicSession(explicitPublicBotId);
    const publicBotId = explicitPublicBotId || storedPublicSession?.publicBotId || null;

    if (!payload?.publicVisitorToken && publicBotId && isPublicWidgetSessionExpired(storedPublicSession, { publicBotId })) {
      storedPublicSession = await initializePublicWidgetSession({ publicBotId });
    }

    const publicVisitorToken = payload?.publicVisitorToken || storedPublicSession?.token || null;
    const isPublicRequest = Boolean(publicBotId || publicVisitorToken);
    const chatbotId = isPublicRequest
      ? null
      : sanitizeChatbotId(
        payload?.chatbotId ||
        getActiveChatbotId({
          token: payload?.userAuthToken || null,
          tenantId: payload?.tenantId || null,
        })
      );

    const endpoint = isPublicRequest ? "/public/chat/message" : "/chat/message";
    const requestBody = sanitizeChatPayload({
      ...payload,
      publicVisitorToken,
      publicBotId,
      chatbotId,
    }, {
      isPublicRequest,
    });

    const headers = buildScopedHeaders({
      token: payload?.userAuthToken || null,
      chatbotId,
      publicBotId: isPublicRequest ? publicBotId : null,
      publicVisitorToken: isPublicRequest ? publicVisitorToken : null,
      tenantId: isPublicRequest ? null : (payload?.tenantId || null),
    });

    const response = await axios.post(`${BACKEND_URL}${endpoint}`, requestBody, { headers });
    return response.data;
  } catch (error) {
    console.error("API Error sendUserInputToChatbot:", extractErrorMessage(error));
    throw error;
  }
}

export async function generateWelcomeMessage(userAuthToken, tenantId = null) {
  try {
    const response = await axios.get(`${BACKEND_URL}/config`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: getActiveChatbotId({
          token: userAuthToken,
          tenantId,
        }),
        tenantId,
      }),
    });

    return {
      aiResponse:
        response.data?.welcomeMessage ||
        "Willkommen! Wie kann ich Ihnen helfen?",
    };
  } catch (error) {
    console.error("API Error generateWelcomeMessage:", extractErrorMessage(error));
    return { aiResponse: "Willkommen! Wie kann ich Ihnen helfen?" };
  }
}

export async function initializePublicWidgetSession({ publicBotId }) {
  try {
    const response = await axios.post(`${BACKEND_URL}/public/widget/init`, { publicBotId }, {
      headers: buildScopedHeaders({ publicBotId }),
    });

    const session = {
      token: response.data?.token || null,
      sessionId: response.data?.sessionId || null,
      publicBotId,
      expiresInSeconds: response.data?.expiresInSeconds || null,
      createdAt: Date.now(),
    };

    if (typeof window !== "undefined") {
      window.__CHATBOT_PUBLIC_CONTEXT__ = {
        publicBotId,
        sessionId: session.sessionId,
        token: session.token,
      };
    }

    setPublicWidgetSession(session, { publicBotId });
    return session;
  } catch (error) {
    console.error("API Error initializePublicWidgetSession:", extractErrorMessage(error));
    throw error;
  }
}

export async function getPublicBotBootstrap(publicBotId) {
  try {
    const response = await axios.get(`${BACKEND_URL}/public/widget/${publicBotId}/bootstrap`, {
      headers: buildScopedHeaders({ publicBotId }),
    });

    return response.data;
  } catch (error) {
    console.error("API Error getPublicBotBootstrap:", extractErrorMessage(error));
    throw error;
  }
}
